import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved, faCircleCheck, faCircleExclamation, faDownload } from '@fortawesome/free-solid-svg-icons';
import api from '../api/client';

// Блок двухфакторной аутентификации в профиле (Epic 7). По желанию:
// включает и выключает сам сотрудник.
function TwoFactorSection() {
    const [status, setStatus] = useState(null);      // { enabled, backupCodesLeft }
    const [setup, setSetup] = useState(null);        // { qr, manualKey }
    const [code, setCode] = useState('');
    const [backupCodes, setBackupCodes] = useState(null); // показываем один раз
    const [disabling, setDisabling] = useState(false);
    const [password, setPassword] = useState('');
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get('/api/profile/2fa')
        .then(r => setStatus(r.data))
        .catch(() => setStatus({ enabled: false }));

    useEffect(() => { load(); }, []);

    const [setupPassword, setSetupPassword] = useState('');

    const startSetup = (e) => {
        e.preventDefault();
        setMsg(null);
        setBusy(true);
        api.post('/api/profile/2fa/setup', { currentPassword: setupPassword })
            .then(r => { setSetup(r.data); setCode(''); setSetupPassword(''); })
            .catch(err => setMsg({ ok: false, message: err.response?.data?.error || 'Failed to start setup' }))
            .finally(() => setBusy(false));
    };

    const enable = (e) => {
        e.preventDefault();
        setMsg(null);
        setBusy(true);
        api.post('/api/profile/2fa/enable', { code })
            .then(r => {
                setBackupCodes(r.data.backupCodes);
                setSetup(null);
                setCode('');
                load();
            })
            .catch(err => setMsg({ ok: false, message: err.response?.data?.error || 'Failed to enable' }))
            .finally(() => setBusy(false));
    };

    const disable = (e) => {
        e.preventDefault();
        setMsg(null);
        setBusy(true);
        api.post('/api/profile/2fa/disable', { currentPassword: password, code })
            .then(() => {
                setDisabling(false);
                setPassword('');
                setCode('');
                setMsg({ ok: true, message: 'Two-factor authentication turned off' });
                load();
            })
            .catch(err => setMsg({ ok: false, message: err.response?.data?.error || 'Failed to disable' }))
            .finally(() => setBusy(false));
    };

    const downloadCodes = () => {
        const text = `Organo Republic DB — backup codes\n\n${backupCodes.join('\n')}\n\n` +
            'Each code works once. Keep them somewhere safe.\n';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
        a.download = 'organo-backup-codes.txt';
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <div className="profile-modal__section">
            <div className="profile-modal__section-title">
                <span className="profile-modal__section-icon"><FontAwesomeIcon icon={faShieldHalved} /></span>
                <h4>Two-Factor Authentication</h4>
            </div>

            {msg && (
                <div className={`profile-modal__status ${msg.ok ? 'is-ok' : 'is-error'}`}>
                    <FontAwesomeIcon icon={msg.ok ? faCircleCheck : faCircleExclamation} />
                    {msg.message}
                </div>
            )}

            {/* Резервные коды показываются ровно один раз после включения. */}
            {backupCodes && (
                <div className="two-fa__backup">
                    <p><strong>Save these backup codes.</strong> Each one works once and lets you
                       sign in if you lose your phone. They will not be shown again.</p>
                    <div className="two-fa__codes">
                        {backupCodes.map(c => <code key={c}>{c}</code>)}
                    </div>
                    <div className="two-fa__actions">
                        <button type="button" className="profile-modal__save-btn" onClick={downloadCodes}>
                            <FontAwesomeIcon icon={faDownload} /> Download
                        </button>
                        <button type="button" className="two-fa__link" onClick={() => setBackupCodes(null)}>
                            I saved them
                        </button>
                    </div>
                </div>
            )}

            {!backupCodes && status?.enabled && !disabling && (
                <>
                    <p className="two-fa__state">
                        <FontAwesomeIcon icon={faCircleCheck} /> Enabled
                        {typeof status.backupCodesLeft === 'number' && (
                            <span className="two-fa__muted"> · {status.backupCodesLeft} backup codes left</span>
                        )}
                    </p>
                    <button type="button" className="two-fa__link" onClick={() => { setDisabling(true); setMsg(null); }}>
                        Turn off
                    </button>
                </>
            )}

            {!backupCodes && status?.enabled && disabling && (
                <form onSubmit={disable}>
                    <div className="profile-modal__row">
                        <label>Current Password</label>
                        <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                    </div>
                    <div className="profile-modal__row">
                        <label>Code from app (or backup code)</label>
                        <input value={code} onChange={e => setCode(e.target.value)} required />
                    </div>
                    <div className="profile-modal__footer">
                        <button type="button" className="two-fa__link" onClick={() => setDisabling(false)}>Cancel</button>
                        <button type="submit" className="profile-modal__save-btn" disabled={busy}>Turn off</button>
                    </div>
                </form>
            )}

            {!backupCodes && status && !status.enabled && !setup && (
                <form onSubmit={startSetup}>
                    <p className="two-fa__state two-fa__muted">
                        Not enabled. Adds a second step at sign-in using an authenticator app
                        (Google Authenticator, Microsoft Authenticator, 1Password — any of them).
                        Signing in with Google skips this step, since Google already checked it.
                    </p>
                    <div className="profile-modal__row">
                        <label>Current Password</label>
                        <input type="password" value={setupPassword}
                            onChange={e => setSetupPassword(e.target.value)} required />
                    </div>
                    <div className="profile-modal__footer">
                        <button type="submit" className="profile-modal__save-btn" disabled={busy}>
                            Set up
                        </button>
                    </div>
                </form>
            )}

            {setup && (
                <form onSubmit={enable}>
                    <p className="two-fa__state">1. Scan this code with your authenticator app:</p>
                    <img src={setup.qr} alt="QR code" className="two-fa__qr" />
                    <p className="two-fa__muted">
                        Can’t scan? Enter this key manually: <code>{setup.manualKey}</code>
                        <br />If you cancel now, delete the entry from your app — it will not work.
                    </p>
                    <div className="profile-modal__row">
                        <label>2. Enter the 6-digit code from the app</label>
                        <input value={code} onChange={e => setCode(e.target.value)}
                            inputMode="numeric" autoComplete="one-time-code" required />
                    </div>
                    <div className="profile-modal__footer">
                        <button type="button" className="two-fa__link" onClick={() => setSetup(null)}>Cancel</button>
                        <button type="submit" className="profile-modal__save-btn" disabled={busy}>
                            Turn on
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}

export default TwoFactorSection;
